import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────
// Voice Selection
// ─────────────────────────────────────────────────────────────

let cachedVoices = [];

function loadVoices() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];

  cachedVoices = window.speechSynthesis.getVoices();

  return cachedVoices;
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
  loadVoices();

  window.speechSynthesis.addEventListener(
    'voiceschanged',
    loadVoices
  );
}

function pickVoice() {
  const voices = cachedVoices.length ? cachedVoices : loadVoices();

  if (!voices.length) return null;

  // Highest priority (Natural/Neural voices)
  const preferred = [
    "Microsoft Aria Online (Natural)",
    "Microsoft Jenny Online (Natural)",
    "Microsoft Ryan Online (Natural)",
    "Microsoft Guy Online (Natural)",
    "Microsoft Ava Online (Natural)",

    "Google US English",
    "Google UK English Female",
    "Google UK English Male",

    "Samantha",
    "Alex",
    "Daniel",
    "Karen",
    "Moira"
  ];

  // Exact preferred voices
  for (const name of preferred) {
    const voice = voices.find(v =>
      v.name.toLowerCase().includes(name.toLowerCase())
    );

    if (voice) return voice;
  }

  // Any Natural / Neural voice
  let voice = voices.find(v =>
    /(natural|neural|online)/i.test(v.name)
  );

  if (voice) return voice;

  // English (India preferred)
  voice = voices.find(v => v.lang === "en-IN");
  if (voice) return voice;

  voice = voices.find(v => v.lang === "en-US");
  if (voice) return voice;

  voice = voices.find(v => v.lang === "en-GB");
  if (voice) return voice;

  return voices.find(v => v.lang.startsWith("en")) || voices[0];
}

// ─────────────────────────────────────────────────────────────
// Speak Helper — tries ElevenLabs via backend, falls back to browser TTS
// ─────────────────────────────────────────────────────────────

let activeAudio = null;

function browserSpeak(text, { onStart, onEnd } = {}) {
  if (!window.speechSynthesis || !text) return;
  const synth = window.speechSynthesis;
  synth.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = 'en-US';
  utter.volume = 1;
  utter.rate = 0.94;
  utter.pitch = 1.02;
  const voice = pickVoice();
  if (voice) {
    utter.voice = voice;
    if (/Google/i.test(voice.name)) { utter.rate = 0.96; utter.pitch = 1.0; }
    if (/Aria|Jenny|Ryan|Guy|Ava/i.test(voice.name)) { utter.rate = 0.94; utter.pitch = 1.0; }
    if (/Samantha|Alex|Daniel/i.test(voice.name)) { utter.rate = 0.93; utter.pitch = 1.03; }
  }
  if (onStart) utter.onstart = onStart;
  if (onEnd) { utter.onend = onEnd; utter.onerror = onEnd; }
  synth.speak(utter);
}

export async function speak(text, { onStart, onEnd } = {}) {
  if (typeof window === 'undefined' || !text) return;

  cancelSpeech();

  const token = localStorage.getItem('no_token');
  if (token) {
    try {
      const res = await fetch('/api/ai/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });

      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('audio')) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          activeAudio = audio;
          if (onStart) onStart();
          audio.onended = () => { URL.revokeObjectURL(url); activeAudio = null; onEnd?.(); };
          audio.onerror = () => { URL.revokeObjectURL(url); activeAudio = null; onEnd?.(); browserSpeak(text, { onStart, onEnd }); };
          audio.play().catch(() => browserSpeak(text, { onStart, onEnd }));
          return;
        }
        // Backend returned { fallback: true } — no ElevenLabs key configured
      }
    } catch { /* network error — fall through to browser TTS */ }
  }

  browserSpeak(text, { onStart, onEnd });
}

export function cancelSpeech() {
  if (activeAudio) {
    try { activeAudio.pause(); activeAudio = null; } catch {}
  }
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

// ─────────────────────────────────────────────────────────────
// Speech Recognition Hook
// ─────────────────────────────────────────────────────────────

export default function useSpeech({ onResult } = {}) {
  const SR =
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const supported =
    !!SR &&
    typeof window !== 'undefined' &&
    !!window.speechSynthesis;

  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState(null);

  const recRef = useRef(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const stop = useCallback(() => {
    try { recRef.current?.abort(); } catch {}
    recRef.current = null;
    setListening(false);
  }, []);

  const start = useCallback(async () => {
    if (!SR || listening) return;

    cancelSpeech();
    setMicError(null);

    // getUserMedia triggers the browser's native permission popup.
    // If the user clicks Allow, we get a stream (which we immediately release),
    // then proceed to start SpeechRecognition.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicError('Microphone access denied. Click the 🔒 icon in your address bar → Site settings → allow Microphone, then try again.');
      } else {
        setMicError(`Microphone unavailable: ${err.message}`);
      }
      return;
    }

    // Permission granted — create a fresh SpeechRecognition instance and start
    try { recRef.current?.abort(); } catch {}

    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.lang = "en-US";

    rec.onresult = (e) => {
      const text = e.results?.[0]?.[0]?.transcript || "";
      if (text.trim()) onResultRef.current?.(text.trim());
    };

    rec.onend = () => {
      recRef.current = null;
      setListening(false);
    };

    rec.onerror = (e) => {
      recRef.current = null;
      setListening(false);
      if (e.error !== 'no-speech') setMicError(`Microphone error: ${e.error}`);
    };

    recRef.current = rec;

    try {
      rec.start();
      setListening(true);
    } catch {
      recRef.current = null;
      setListening(false);
    }
  }, [SR, listening]);

  // Clean up on unmount
  useEffect(() => {
    return () => { try { recRef.current?.abort(); } catch {} };
  }, []);

  return { supported, listening, start, stop, micError, clearMicError: () => setMicError(null) };
}